import React, { useState } from 'react';
import { GetStartedButton } from '../common/GetStartedButton';
import { SegmentedToggle } from '../common/SegmentedToggle';

// Prices and features confirmed by Ian (Oct 29, 2026). The billing system of
// record is Outseta (public /payment/plans endpoint); if prices change there,
// update them here too, or switch to paymentService.getPlans(role).
const AUDIENCES = {
  coach: {
    label: 'Coaches',
    cta: 'GET COACH ACCESS',
    plans: [
      {
        name: 'Monthly',
        price: 49,
        interval: 'month',
        features: [
          'Sortable stats for every JUCO player',
          'Player highlight video library',
          'Uncommitted Tracker',
          'Saved filters & private notes',
          'Follow players and teams',
          'Message players directly',
        ],
      },
      {
        name: 'Annual',
        price: 490,
        interval: 'year',
        badge: 'Best value',
        features: [
          'Everything in Monthly',
          '2 months free',
          'Request more video on any player',
          'Priority support',
        ],
      },
    ],
  },
  player: {
    label: 'Players',
    cta: 'JOIN AS A PLAYER',
    plans: [
      {
        name: 'Monthly',
        price: 9.99,
        interval: 'month',
        features: [
          'Verified player profile',
          'Stats synced from your season',
          'Upload highlight video',
          'Get found by college coaches',
          'Message coaches who follow you',
        ],
      },
      {
        name: 'Annual',
        price: 99,
        interval: 'year',
        badge: 'Best value',
        features: [
          'Everything in Monthly',
          '2 months free',
          'See which coaches follow you',
          'Priority profile review',
        ],
      },
    ],
  },
};

const formatPrice = (price) =>
  `$${Number(price).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

// Plan cards are navy in both themes (contrast against the light page),
// so the text colors inside them are fixed rather than theme tokens.
const CheckIcon = () => (
  <svg className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

export const PricingBox = () => {
  const [audience, setAudience] = useState('coach');
  const { plans, cta } = AUDIENCES[audience];

  return (
    <div className="w-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink">
          Gain <span className="text-jp-accent">Full Access</span>
        </h2>
        <div className="flex items-center gap-3">
          <SegmentedToggle
            ariaLabel="Plan audience"
            options={Object.entries(AUDIENCES).map(([key, item]) => ({ value: key, label: item.label }))}
            value={audience}
            onChange={setAudience}
          />
        </div>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`relative flex flex-col rounded-2xl p-6 sm:p-8 bg-jp-band text-white border ${
              plan.badge ? 'border-jp-accent ring-1 ring-jp-accent' : 'border-jp-outline/70'
            }`}
          >
            {plan.badge && (
              <span className="absolute -top-3 right-6 bg-jp-gold text-jp-navy text-[10px] font-bold uppercase tracking-wider rounded-full px-3 py-1">
                {plan.badge}
              </span>
            )}
            <p className={`text-slate-300 text-sm font-bold uppercase tracking-wider mb-2 ${plan.badge ? 'dark:text-jp-gold' : ''}`}>{plan.name}</p>
            <p className="mb-6">
              <span className="text-5xl font-extrabold text-cyan-400 tabular-nums">{formatPrice(plan.price)}</span>
              <span className="text-slate-300 text-sm"> / {plan.interval}</span>
            </p>
            <ul className="space-y-2.5">
              {plan.features.map((feature) => (
                <li key={feature} className="flex gap-2 text-slate-200 text-sm">
                  <CheckIcon />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-center">
        <GetStartedButton>{cta}</GetStartedButton>
      </div>
    </div>
  );
};

export default PricingBox;
